// ABOUTME: Renders a custom page layout as a CSS grid with columns and widgets.
// ABOUTME: Resolves widget types from the registry, hides widgets whose config.roles the user lacks, and renders them inside Suspense boundaries.

import { Box, ThreeViewLayout, useTheme } from '@ds/coolshop';
import { usePimSidebarOpen } from '@src/_new/hooks/usePimSidebarOpen';
import { useAuth } from '@src/hooks';
import { Suspense } from 'react';

import { CustomPageLayout, CustomPageWidget } from '../types';
import { WidgetFrame } from '../widgets/components/WidgetFrame';
import { WIDGET_TYPE_SKELETON_VARIANT, WidgetSkeleton } from '../widgets/components/WidgetSkeleton';
import widgetRegistry from '../widgets/registry';
import { WidgetProps } from '../widgets/types';
import { isWidgetVisibleForRoles } from '../widgets/utils/widgetRoles';

interface LayoutRendererProps {
    layout: CustomPageLayout;
    context: Omit<WidgetProps, 'config'>;
}
interface LayoutColumnProps {
    column: CustomPageLayout['columns'][number];
    context: Omit<WidgetProps, 'config'>;
    gap?: number;
}

// The widgets that lay a whole page out and fill its height when told to (`fillHeight`).
const FILL_HEIGHT_WIDGET_TYPES: ReadonlySet<string> = new Set(['model-selection']);

// A CSS length usable as a side column's width: the `fr` fractions columns may carry mean
// nothing to a fixed-width panel, so they keep the default width.
const COLUMN_LENGTH_PATTERN = /^\d+(\.\d+)?(px|%|rem|em|vw)$/;

/** The side panel width a column asks for, or undefined to keep the layout's default. */
function parseColumnWidth(width?: string | null): string | undefined {
    const raw = width?.trim();
    return raw && COLUMN_LENGTH_PATTERN.test(raw) ? raw : undefined;
}

function parseWidgetWidth(config?: Record<string, unknown>): number {
    const raw = config?.width;
    if (typeof raw === 'number' && raw > 0 && raw <= 1) return raw;
    return 1;
}

interface WidgetMargin {
    mx?: number;
    my?: number;
    mt?: number;
    mb?: number;
    ml?: number;
    mr?: number;
}

function parseWidgetMargin(config?: Record<string, unknown>): WidgetMargin | undefined {
    const raw = config?.margin;
    if (!raw || typeof raw !== 'object') return undefined;
    const margin = raw as Record<string, unknown>;
    const result: WidgetMargin = {};
    if (typeof margin.x === 'number') result.mx = margin.x;
    if (typeof margin.y === 'number') result.my = margin.y;
    if (typeof margin.t === 'number') result.mt = margin.t;
    if (typeof margin.b === 'number') result.mb = margin.b;
    if (typeof margin.l === 'number') result.ml = margin.l;
    if (typeof margin.r === 'number') result.mr = margin.r;
    return Object.keys(result).length > 0 ? result : undefined;
}

interface WidgetPadding {
    px?: number;
    py?: number;
    pt?: number;
    pb?: number;
    pl?: number;
    pr?: number;
}

// Breathing room around every widget of the grid, in MUI spacing units: 8px horizontally
// and 6px vertically. A widget's own `padding` config overrides it per side.
const DEFAULT_CELL_PADDING: WidgetPadding = { px: 1, py: 0.75 };

function parseWidgetPadding(config?: Record<string, unknown>): WidgetPadding {
    const raw = config?.padding;
    if (!raw || typeof raw !== 'object') return DEFAULT_CELL_PADDING;
    const padding = raw as Record<string, unknown>;
    const result: WidgetPadding = { ...DEFAULT_CELL_PADDING };
    if (typeof padding.x === 'number') result.px = padding.x;
    if (typeof padding.y === 'number') result.py = padding.y;
    if (typeof padding.t === 'number') result.pt = padding.t;
    if (typeof padding.b === 'number') result.pb = padding.b;
    if (typeof padding.l === 'number') result.pl = padding.l;
    if (typeof padding.r === 'number') result.pr = padding.r;
    return result;
}

function renderWidget(
    widget: CustomPageWidget,
    context: Omit<WidgetProps, 'config'>,
    widgetIndex: number,
    stickyZIndex: number,
) {
    const WidgetComponent = widgetRegistry[widget.type];
    if (!WidgetComponent) return null;

    const widgetConfig = widget.config ? (widget.config as Record<string, unknown>) : undefined;
    const width = parseWidgetWidth(widgetConfig);
    const margin = parseWidgetMargin(widgetConfig);
    // A widget filling a full-bleed page runs edge to edge, so only its own padding applies there.
    const padding = context.fillHeight ? {} : parseWidgetPadding(widgetConfig);
    const sticky = widgetConfig?.sticky === true;

    // Widgets are lazy-loaded, so the cell holds a framed placeholder while the chunk downloads
    // instead of collapsing to nothing. The widget types with no framed loading state of their
    // own keep an empty fallback, so nothing appears only to disappear once they mount.
    const skeletonVariant = WIDGET_TYPE_SKELETON_VARIANT[widget.type];

    return (
        /* Grid cell: it only positions and spaces the widget, and it sits outside the Suspense
           boundary so the fallback is laid out at the same width and padding as the widget.
           `bgColor`/`textColor` are applied by the widget's own WidgetFrame, to the container
           below the title. */
        <Box
            key={widgetIndex}
            width={width}
            height={context.fillHeight ? '100%' : undefined}
            {...margin}
            {...padding}
            // A pinned widget stays at the top of the scroll container while the rest of
            // the layout scrolls under it, so it has to outrank the sticky headers of the
            // data tables rendered below it (MUI stickyHeader cells sit at z-index 2).
            {...(sticky ? { position: 'sticky', top: 0, zIndex: stickyZIndex } : {})}
        >
            <Suspense
                fallback={
                    skeletonVariant ? (
                        <WidgetFrame config={widgetConfig}>
                            <WidgetSkeleton
                                variant={skeletonVariant}
                                minHeight={skeletonVariant === 'block' ? 400 : undefined}
                            />
                        </WidgetFrame>
                    ) : null
                }
            >
                <WidgetComponent {...context} config={widgetConfig} />
            </Suspense>
        </Box>
    );
}

function LayoutColumn({ column, context, gap }: LayoutColumnProps) {
    const theme = useTheme();
    const stickyZIndex = theme.zIndex.appBar;
    const { hasUserRole } = useAuth();
    // Widgets restricted by `roles` are dropped before layout, so they leave no empty cell or spacer.
    const visibleWidgets = column.widgets.filter(w =>
        isWidgetVisibleForRoles(
            w.config as Record<string, unknown> | null | undefined,
            hasUserRole,
        ),
    );
    const topWidgets = visibleWidgets.filter(w => w.config?.position === 'top');
    const middleWidgets = visibleWidgets.filter(
        w => !w.config?.position || (w.config.position !== 'top' && w.config.position !== 'bottom'),
    );
    const bottomWidgets = visibleWidgets.filter(w => w.config?.position === 'bottom');
    const orderedWidgets = [...topWidgets, ...middleWidgets];
    const hasBottom = bottomWidgets.length > 0;

    // On a full-bleed page, a column holding one widget that lays the page out itself (the
    // catalog) fills the page height edge to edge. Any other column scrolls as usual and gets
    // the breathing room the page no longer gives it.
    const fillsPage =
        !!context.fillHeight &&
        visibleWidgets.length === 1 &&
        FILL_HEIGHT_WIDGET_TYPES.has(visibleWidgets[0].type);
    const columnContext = { ...context, fillHeight: fillsPage };
    const columnPadding = context.fillHeight && !fillsPage ? { px: 3, py: 2 } : {};

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={gap}
            height={hasBottom || fillsPage ? '100%' : undefined}
            {...columnPadding}
        >
            <Box display="flex" flexWrap="wrap" gap={gap} height={fillsPage ? '100%' : undefined}>
                {orderedWidgets.map((w, i) => renderWidget(w, columnContext, i, stickyZIndex))}
            </Box>
            {hasBottom && <Box flex={1} />}
            {hasBottom && (
                <Box display="flex" flexWrap="wrap" gap={gap}>
                    {bottomWidgets.map((w, i) => renderWidget(w, columnContext, i, stickyZIndex))}
                </Box>
            )}
        </Box>
    );
}

export function LayoutRenderer({ layout, context }: LayoutRendererProps) {
    const sidebarOpen = usePimSidebarOpen();
    const hasLeftColumn = layout.columns.length > 1;
    // A third column is the right side panel, as the first one of two or more is the left one.
    const rightColumn = layout.columns.length > 2 ? layout.columns[2] : undefined;
    // A side column's width, when it is a CSS length, sizes its panel.
    const leftWidth = hasLeftColumn ? parseColumnWidth(layout.columns[0].width) : undefined;
    const rightWidth = rightColumn ? parseColumnWidth(rightColumn.width) : undefined;

    return (
        <ThreeViewLayout
            overrideLeftWidth={leftWidth}
            leftOpen={sidebarOpen}
            overrideRightWidth={rightWidth}
            left={
                hasLeftColumn ? (
                    <Box height="100%" overflowY="auto">
                        <LayoutColumn column={layout.columns[0]} context={context} />
                    </Box>
                ) : undefined
            }
            center={
                hasLeftColumn ? (
                    <Box height="100%" overflowY="auto">
                        <LayoutColumn column={layout.columns[1]} context={context} gap={3} />
                    </Box>
                ) : (
                    <LayoutColumn column={layout.columns[0]} context={context} />
                )
            }
            right={
                rightColumn ? (
                    <Box height="100%" overflowY="auto">
                        <LayoutColumn column={rightColumn} context={context} />
                    </Box>
                ) : undefined
            }
        />
    );
}
